// Token renewal service for session-based authentication

import { logDebug } from '../utils/errorLogger';
class TokenRenewalService {
  private renewalInterval: NodeJS.Timeout | null = null;
  private readonly RENEWAL_INTERVAL = 30 * 60 * 1000; // 30 minutes
  private isActive = false;

  constructor() {
    this.startRenewal = this.startRenewal.bind(this);
    this.stopRenewal = this.stopRenewal.bind(this);
    this.renewToken = this.renewToken.bind(this);
  }

  /**
   * Start automatic token renewal
   */
  startRenewal() {
    if (this.isActive) return;
    
    this.isActive = true;
    this.renewalInterval = setInterval(async () => {
      await this.renewToken();
    }, this.RENEWAL_INTERVAL);
    
    logDebug('Token renewal service started');
  }

  /**
   * Stop automatic token renewal
   */
  stopRenewal() {
    if (this.renewalInterval) {
      clearInterval(this.renewalInterval);
      this.renewalInterval = null;
    }
    this.isActive = false;
    logDebug('Token renewal service stopped');
  }

  /**
   * Manually renew token
   */
  async renewToken(): Promise<boolean> {
    const auth = localStorage.getItem('cascade-auth');
    if (auth !== 'authenticated') {
      return false;
    }

    try {
      const response = await fetch('/api/auth/status', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.authenticated) {
          // Session is still valid, update local timestamp
          localStorage.setItem('cascade-auth-time', Date.now().toString());
          logDebug('Token renewed successfully');
          return true;
        } else {
          // Session expired on server
          logDebug('Session expired on server');
          localStorage.removeItem('cascade-auth');
          localStorage.removeItem('cascade-auth-time');
          this.stopRenewal();
          return false;
        }
      } else {
        // Don't log to Application Insights - this is normal auth flow behavior
        console.warn('Token renewal failed - server error');
        return false;
      }
    } catch (error) {
      // Don't log to Application Insights - token renewal failures are expected
      console.warn('Token renewal failed:', error);
      return false;
    }
  }

  /**
   * Check if renewal service is active
   */
  isRenewalActive(): boolean {
    return this.isActive;
  }
}

// Export singleton instance
export const tokenRenewalService = new TokenRenewalService();
